<template>
  <div data-cy="register-step-form">
    <div class="Vlt-card__image Vlt-bg-black">
      <div class="Vlt-grid Vlt-white">
        <div v-for="step in steps" :key="step.key"
             :class="{ 'Vlt-col Vlt-left step': true, 'step--active': step.key === currentStep }">
                    <span v-if="isStepComplete(step.key) && !step.error">
                         <vlt-icon icon="check-circle-negative" class="Vlt-green" small color="green"/>
                    </span>
          <span v-else-if="step.error">
                         <vlt-icon icon="cross-circle" class="Vlt-green" small color="red"/>
                    </span>
          <span v-else class="Vlt-number step-number" :data-index="step.key"></span>
          <span class="step-label">{{ step.label }}</span>
        </div>
      </div>
      <div class="Vlt-progress">
        <div
            class="Vlt-progress__bar"
            data-cy="progress-bar"
            :style="{ width: `${progressWith}%`}"
            role="progressbar"
            aria-valuenow="0"
            aria-valuemin="0"
            aria-valuetext="Step 1: example"
            aria-valuemax="100"
        ></div>
      </div>
    </div>
    <vwc-banner
        v-if="!readOnly"
        connotation="warning"
        open=""
        message="Exiting the form will delete all changes made"
    ></vwc-banner>
    <div class="progress-content">
      <div v-if="isUpdatesRequired" :class="['updates-required-banner', updatesRequired.connotation]">
        <div class="left-side-container">
          <vwc-icon class="icon-updates-required" :type="updatesRequired.icon"></vwc-icon>
          <p>{{ updatesRequired.text }}</p>
        </div>
        <cd-button
            class="updates-required-button"
            connotation="primary"
            :layout="getLayout"
            label="View Required Changes"
            :icon="getIcon"
            @click="toggleButton"
        />
      </div>
      <slot :name="currentStepSlot"></slot>
      <div class="footer-buttons">
        <slot name="footer-buttons"></slot>
        <cd-button
            v-if="cancelButton"
            data-cy="cancel-button"
            class="cancel-button"
            layout="outlined"
            connotation="primary"
            :label="$t('common.cancelBtnLabel')"
            @click="$emit('cancel')"
        ></cd-button>
        <cd-button
            class="back-button"
            layout="outlined"
            data-cy="back-button"
            connotation="primary"
            v-if="currentStep > 1"
            :label="$t('common.backButtonLabel')"
            @click="back"
        ></cd-button>
        <cd-button
            v-if="nextStepShown"
            layout="filled"
            data-cy="next-button"
            :connotation="nextStepLabel === this.$t('smsRegistrations.submitAndPay') ? 'cta' : 'primary'"
            :label="nextStepLabel"
            :disabled="nextStepDisabled || registerInProgress"
            @click="nextStep"
        >
          <cd-spinner v-if="registerInProgress" slot="trailingIcon" class="inner-spinner" indeterminate
                      :density="-7.5"></cd-spinner>
        </cd-button>
      </div>
    </div>
    <vwc-snackbar
        :connotation="'alert'"
        icon="megaphone-solid"
        :message="this.$t('smsRegistrations.submitAndPayError')"
        :timeoutMs="10000"
        ref="errSnackbar"
        data-cy="reg-error-snack"
    />
  </div>
</template>

<script>
import {mapActions, mapState} from 'vuex';
import {VltIcon} from '@vonagevolta/vue';
import CdButton from '../components/atomicUIComponents/button/CdButton.vue';
import CdSpinner from "./atomicUIComponents/spinner/CdSpinner";
import {REGISTER_STEP_FORM_TYPE} from "../helpers/smsRegisrtration/constants";

const FORM_TYPES = [
  REGISTER_STEP_FORM_TYPE.QUICK_FLOW,
  REGISTER_STEP_FORM_TYPE.UPDATES_REQUIRED,
  REGISTER_STEP_FORM_TYPE.EDIT_APPROVED_REGISTRATION,
];

export default {
  name: "register-step-form",
  components: {VltIcon, CdButton, CdSpinner},
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
    currentStep: {
      type: Number,
      required: true,
    },
    steps: {
      type: Array,
      required: true
    },
    registerAndPayDisabled: {
      type: Boolean,
      default: false
    },
    registerInProgress: {
      type: Boolean,
      default: false
    },
    registeredTypeName: {
      type: String,
      default: ''
    },
    nextButtonDisabled: {
      type: Boolean,
      default: false
    },
    cancelButton: {
      type: Boolean,
      default: true
    },
    isUpdatesRequired: {
      type: Boolean,
      default: false
    },
  },
  methods: {
    ...mapActions("smsRegistrations", ["toggleShowRequiredChanges"]),
    isStepComplete(index) {
      return this.currentStep > index;
    },
    nextStep() {
      if (this.currentStep < this.steps.length) {
        this.$emit('nextStep');
      } else {
        this.$emit('registerAndPay');
      }
    },
    back() {
      this.$emit('previousStep');
    },
    toggleButton() {
      this.toggleShowRequiredChanges(!this.showRequiredChanges)
    },
  },
  computed: {
    ...mapState(['showPricing']),
    ...mapState("smsRegistrations", ["showRequiredChanges", "registrationError"]),
    progressWith() {
      return (this.currentStep - 1) / this.steps.length * 100;
    },
    currentStepSlot() {
      return `step-body-${this.currentStep}`
    },
    nextStepLabel() {
      if (FORM_TYPES.includes(this.registeredTypeName)) {
        if (this.currentStep === this.steps.length) {
          return this.registeredTypeName === REGISTER_STEP_FORM_TYPE.QUICK_FLOW ? this.$t('smsRegistrations.submitAndPay') : this.$t('smsRegistrations.updateRequiredFormDone')
        }
        return this.$t('common.next');
      } else {
        const registerLabel = this.showPricing ? this.$t('brands.registerAndPay') : `${this.$t('common.register')} ${this.registeredTypeName}`;
        if (this.currentStep >= this.steps.length - 1) {
          return this.currentStep === this.steps.length - 1 ? this.$t('brands.reviewBtnLabel') : registerLabel
        }
        return this.$t('common.next');
      }
    },
    nextStepDisabled() {
      if ((this.currentStep === this.steps.length && this.registerAndPayDisabled) || this.nextButtonDisabled) {
        return true;
      }
      return false;
    },
    nextStepShown() {
      if (this.readOnly && this.currentStep === this.steps.length) {
        return false;
      }
      return true;
    },
    currentStepKey() {
      return this.steps[this.currentStep - 1].key;
    },
    currentStepLabel() {
      return this.steps[this.currentStep - 1].label;
    },
    getIcon() {
      return this.showRequiredChanges ? "eye-hide-line" : "eye-line";
    },
    getLayout() {
      return this.showRequiredChanges ? "outlined" : "filled";
    },
    regError() {
      return this.registrationError;
    },
    updatesRequired() {
      switch(this.registeredTypeName) {
        case REGISTER_STEP_FORM_TYPE.EDIT_APPROVED_REGISTRATION:
          return {
            icon: 'info',
            text: this.$t('smsRegistrations.approvedRegistrationUpdatesRequiredStepFormView'),
            connotation: 'alert',
          };
        default:
          return {
            icon: 'warning-line',
            text: this.$t('smsRegistrations.updatesRequiredStepFormView'),
            connotation: 'warning',
          };
      }
    },
  },
  watch: {
    regError(newErr) {
      if (newErr !== null) {
        this.$refs.errSnackbar.show();
      }
    },
    updatesRequired() {
      switch(this.registeredTypeName) {
        case REGISTER_STEP_FORM_TYPE.EDIT_APPROVED_REGISTRATION:
          return {
            icon: 'info',
            text: this.$t('smsRegistrations.approvedRegistrationUpdatesRequiredStepFormView'),
            connotation: 'alert',
          };
        default:
          return {
            icon: 'warning-line',
            text: this.$t('smsRegistrations.updatesRequiredStepFormView'),
            connotation: 'warning',
          };
      }
    }
  }
}
</script>

<style scoped>
@media screen and (max-width: 767px) {
  .step:not(.step--active) {
    display: none;
  }

  .step.step--active {
    margin-left: 24px;
    align-items: unset;
  }

  .Vlt-progress {
    display: none;
  }

  .updates-required-banner {
    flex-wrap: wrap;
  }

  .updates-required-button {
    width: 100%;
  }
}

.Vlt-card__image {
  height: 100px;
}

.Vlt-card__image .Vlt-grid {
  margin-top: 5px;
}

.Vlt-progress {
  margin-top: 13px;
}

.Vlt-card__content {
  padding: 16px 73px;
}

.step-number {
  background-color: black;
  width: 1.8rem;
  height: 1.8rem;
  border-color: rgb(153, 153, 153);
  border-left-style: solid;
  border-left-width: thin;
  border-right-style: solid;
  border-right-width: thin;
  border-top-style: solid;
  border-top-width: thin;
  border-bottom-style: solid;
  border-bottom-width: thin;
  font-size: 1.2rem;
  margin-right: 0.8rem;
}

.step-label {
  vertical-align: middle;
}

.step {
  color: rgb(153, 153, 153);
}

.step .Vlt-icon {
  display: inline-block;
  height: 1.8rem;
  vertical-align: middle;
  width: 1.8rem;
  margin: 4px 0.8rem 4px 4px;
}

.step .Vlt-icon svg {
  fill: #06ba77 !important;
  height: 1.8rem;
  width: 1.8rem;
}

.step-number {
  color: rgb(153, 153, 153);
  border-color: rgb(153, 153, 153);
}

.step--active {
  font-weight: 600;
  color: white;
}

.step--active .step-number {
  color: white;
  border-color: white;
}

.hidden {
  display: none;
}

.Vlt-form__element:not(.Vlt-form__element--error) .Vlt-input input:invalid {
  border-color: #e1e2e6;
  background: none;
}

.Vlt-card__image {
  margin: 0 0 0 0 !important;
}

.Vlt-card__image .Vlt-grid {
  width: calc(100% - 24px) !important;
}

.Vlt-card__image .Vlt-progress {
  width: calc(100% - 54px) !important;
}

.progress-content {
  background: white;
  border-bottom-right-radius: 8px;
  border-bottom-left-radius: 8px;
  padding: 1px;
}

.footer-buttons {
  display: flex;
  justify-content: flex-end;
  margin: 0 24px 24px;
  gap: 8px;
}

.inner-spinner {
  margin-left: 8px;
}

.updates-required-banner {
  border-radius: 6px;
  padding: 20px;
  padding-bottom: 16px;
  display: flex;
  flex-direction: row;
  margin: 22px;
  align-items: center;
  justify-content: space-between;
}

.updates-required-banner.warning {
  background: #fdf5d4;
  border: 1px solid #be5702;
}

.updates-required-banner.alert {
  background: #E8F4FB;
  border: 1px solid #0276D5;
}


.left-side-container {
  display: flex;
  flex-direction: row;
}

.updates-required-button {
  text-wrap: nowrap;
}

.icon-updates-required {
  min-height: 20px;
  min-width: 20px;
  margin-right: 16px;
}

.warning .icon-updates-required {
  color: #be5702;
}

.alert .icon-updates-required {
  color: #0276D5;
}
</style>
